/**
 * WorkflowDashboardPage — Kanban-style operations board.
 *
 * Columns are dynamically derived from the backend state-machine metadata
 * (one column per state, terminal states collapsed at the right).
 * Filters: type, date range, gestor search. Click a card to open
 * OperationDetailDrawer.
 *
 * Drag-and-drop is intentionally NOT wired up — the column structure is
 * ready for react-beautiful-dnd / dnd-kit when the user enables it.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import {
  Filter, RefreshCw, Plus, Search, LayoutGrid, Calendar,
} from 'lucide-react';
import {
  fetchStateMachines, listOperations, createOperation,
} from '@/services/workflowApi';
import StateBadge from '@/components/workflow/StateBadge';
import OperationDetailDrawer from '@/components/workflow/OperationDetailDrawer';

const WorkflowDashboardPage = () => {
  const { getAuthHeaders } = useAuth();
  const [stateMachines, setStateMachines] = useState(null);
  const [opType, setOpType] = useState('remittance');
  const [operations, setOperations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [drawerId, setDrawerId] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const loadMachines = useCallback(async () => {
    try {
      const m = await fetchStateMachines(getAuthHeaders);
      setStateMachines(m);
    } catch (e) {
      toast.error('No se pudo cargar el state machine');
    }
  }, [getAuthHeaders]);

  const loadOps = useCallback(async () => {
    setLoading(true);
    try {
      const r = await listOperations(getAuthHeaders, { type: opType, limit: 500 });
      setOperations(r.operations || []);
    } catch (e) {
      toast.error('No se pudieron cargar las operaciones');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, opType]);

  useEffect(() => { loadMachines(); }, [loadMachines]);
  useEffect(() => { loadOps(); }, [loadOps]);

  const columns = useMemo(() => {
    if (!stateMachines?.[opType]) return [];
    return stateMachines[opType].states;
  }, [stateMachines, opType]);

  const grouped = useMemo(() => {
    const filt = operations.filter((o) => {
      if (!search) return true;
      const q = search.toLowerCase();
      return [o.id, o.client_id, o.gestor_id, o.route_origin, o.route_destination, o.passenger_names?.join(' ')]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });
    const m = {};
    columns.forEach((c) => { m[c] = []; });
    filt.forEach((o) => { (m[o.state] = m[o.state] || []).push(o); });
    return m;
  }, [operations, columns, search]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <LayoutGrid className="w-6 h-6 text-blue-600" />
            Tablero de Operaciones
          </h1>
          <p className="text-xs text-slate-500">Kanban operacional por estado · {operations.length} operaciones</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex bg-slate-100 rounded-lg p-1">
            {['remittance', 'ticket'].map((t) => (
              <button
                key={t}
                onClick={() => setOpType(t)}
                data-testid={`type-tab-${t}`}
                className={`px-3 py-1 text-xs font-semibold rounded ${opType === t ? 'bg-white text-slate-900 shadow' : 'text-slate-500'}`}
              >
                {t === 'remittance' ? 'Remesas' : 'Tickets'}
              </button>
            ))}
          </div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar (id, ruta, pasajero…)"
              className="pl-7 h-8 w-64 text-xs"
              data-testid="workflow-search"
            />
          </div>
          <Button size="sm" variant="outline" onClick={loadOps} data-testid="workflow-refresh"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refrescar</Button>
          <Button size="sm" onClick={() => setShowNew(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="workflow-new"><Plus className="w-3.5 h-3.5 mr-1" /> Nueva</Button>
        </div>
      </div>

      {/* Kanban board */}
      <div className="overflow-x-auto pb-3">
        <div className="flex gap-3" style={{ minWidth: 'fit-content' }}>
          {columns.map((state) => {
            const items = grouped[state] || [];
            const isTerminal = stateMachines[opType].terminal.includes(state);
            return (
              <div key={state} className="w-72 shrink-0" data-testid={`column-${state}`}>
                <div className={`px-3 py-2 rounded-t-lg flex items-center justify-between border-b-2 ${isTerminal ? 'border-slate-300 bg-slate-100' : 'border-blue-400 bg-white'}`}>
                  <StateBadge state={state} />
                  <span className="text-[10px] font-bold text-slate-500" data-testid={`column-${state}-count`}>{items.length}</span>
                </div>
                <div className="bg-slate-50 rounded-b-lg p-2 space-y-2 min-h-[200px]">
                  {loading && items.length === 0 ? (
                    <div className="h-16 rounded animate-pulse bg-slate-200/50" />
                  ) : items.length === 0 ? (
                    <p className="text-[10px] text-slate-400 italic text-center py-3">vacío</p>
                  ) : items.map((o) => (
                    <button
                      key={o.id}
                      onClick={() => setDrawerId(o.id)}
                      data-testid={`op-card-${o.id}`}
                      className="w-full text-left p-2.5 bg-white rounded border border-slate-200 hover:border-blue-400 hover:shadow-sm transition-all"
                    >
                      <p className="text-[10px] font-mono text-slate-400 truncate">{o.id.slice(0,8)}…</p>
                      <p className="text-xs font-semibold text-slate-900 truncate mt-0.5">
                        {o.route_origin && o.route_destination ? `${o.route_origin} → ${o.route_destination}` : (o.passenger_names?.[0] || 'Operación')}
                      </p>
                      <div className="flex items-center justify-between mt-1.5">
                        <span className="text-[10px] text-slate-500">{o.amount ? `$${o.amount}` : '—'}</span>
                        <span className="text-[10px] text-slate-400 flex items-center gap-0.5">
                          <Calendar className="w-2.5 h-2.5" />
                          {new Date(o.updated_at).toLocaleDateString()}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {drawerId && (
        <OperationDetailDrawer
          operationId={drawerId}
          stateMachines={stateMachines}
          onClose={() => setDrawerId(null)}
          onChanged={loadOps}
        />
      )}

      {showNew && (
        <NewOperationModal
          opType={opType}
          getAuthHeaders={getAuthHeaders}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); loadOps(); }}
        />
      )}
    </div>
  );
};

const NewOperationModal = ({ opType, getAuthHeaders, onClose, onCreated }) => {
  const [form, setForm] = useState({
    type: opType, amount: '', currency_from: 'USD', currency_to: 'UYU',
    route_origin: '', route_destination: '', passenger_names: '',
  });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form };
      payload.amount = form.amount ? Number(form.amount) : undefined;
      payload.passenger_names = form.passenger_names ? form.passenger_names.split(',').map((s) => s.trim()).filter(Boolean) : undefined;
      await createOperation(getAuthHeaders, payload);
      toast.success('Operación creada');
      onCreated?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="new-op-modal">
      <Card className="w-full max-w-md p-5 bg-white space-y-3">
        <h3 className="text-lg font-bold text-slate-900">Nueva operación · {opType}</h3>
        <Input placeholder="Monto" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="new-op-amount" />
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Moneda origen" value={form.currency_from} onChange={(e) => setForm({ ...form, currency_from: e.target.value })} data-testid="new-op-curr-from" />
          <Input placeholder="Moneda destino" value={form.currency_to} onChange={(e) => setForm({ ...form, currency_to: e.target.value })} data-testid="new-op-curr-to" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Ruta origen" value={form.route_origin} onChange={(e) => setForm({ ...form, route_origin: e.target.value })} data-testid="new-op-route-from" />
          <Input placeholder="Ruta destino" value={form.route_destination} onChange={(e) => setForm({ ...form, route_destination: e.target.value })} data-testid="new-op-route-to" />
        </div>
        <Input placeholder="Pasajeros (coma)" value={form.passenger_names} onChange={(e) => setForm({ ...form, passenger_names: e.target.value })} data-testid="new-op-passengers" />
        <div className="flex gap-2 justify-end pt-2">
          <Button variant="outline" onClick={onClose} disabled={busy} data-testid="new-op-cancel">Cancelar</Button>
          <Button onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="new-op-submit">{busy ? 'Creando…' : 'Crear'}</Button>
        </div>
      </Card>
    </div>
  );
};

export default WorkflowDashboardPage;
